import type { DocNode as ADF } from '@atlaskit/adf-schema';
import type { User } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-details.tsx';
import type { CommentRoleType } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import type { IssueId, IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';

type ChangeEventType = 'CHANGE_EVENT';
export const CHANGE_EVENT: ChangeEventType = 'CHANGE_EVENT';

type IncidentEventType = 'INCIDENT_EVENT';
export const INCIDENT_EVENT: IncidentEventType = 'INCIDENT_EVENT';

type ObjectUpdateEventType = 'OBJECT_UPDATE_EVENT';
export const OBJECT_UPDATE_EVENT: ObjectUpdateEventType = 'OBJECT_UPDATE_EVENT';

type ObjectCommentEventType = 'OBJECT_COMMENT_EVENT';
export const OBJECT_COMMENT_EVENT: ObjectCommentEventType = 'OBJECT_COMMENT_EVENT';

export type TimelineEventType =
	| ChangeEventType
	| IncidentEventType
	| ObjectUpdateEventType
	| ObjectCommentEventType;

export const TIMELINE_EVENT_TYPES: TimelineEventType[] = [
	CHANGE_EVENT,
	INCIDENT_EVENT,
	OBJECT_UPDATE_EVENT,
	OBJECT_COMMENT_EVENT,
];

type TimelineEventBase<E extends TimelineEventType, T> = {
	type: E;
	timestamp: number;
	location?: string;
	data: T;
};

type RequestAttributes = {
	id: IssueId;
	issueSummary: string;
	issueStatus: string;
	issueKey: IssueKey;
	statusCategoryId: number;
	statusCategory: string;
	issueTypeIconUrl: string;
	issueTypeName?: string;
	issuePriorityUrl?: string;
	issuePriorityName?: string;
};

export type ObjectChangeType =
	| 'object-created'
	| 'added-attachment'
	| 'deleted-attachment'
	| 'added-avatar'
	| 'changed-avatar'
	| 'deleted-avatar'
	| 'attribute-updated'
	| 'object-type-updated';

type ObjectUpdateEvent = {
	id: string;
	actor: User;
	attributeName: string;
	isUserAttribute: boolean;
	oldValue: string;
	newValue: string;
	changeType: ObjectChangeType;
};

export type ObjectCommentEvent = {
	id: string;
	actor: User;
	comment: ADF;
	role: CommentRoleType;
	canEdit: boolean;
	canDelete: boolean;
	isSaving?: boolean;
	isEdited: boolean;
	editedTimestamp: number;
};

export type ChangeTimelineEvent = TimelineEventBase<ChangeEventType, RequestAttributes>;

export type IncidentTimelineEvent = TimelineEventBase<IncidentEventType, RequestAttributes>;

export type ObjectUpdateTimelineEvent = TimelineEventBase<ObjectUpdateEventType, ObjectUpdateEvent>;

export type ObjectCommentTimelineEvent = TimelineEventBase<
	ObjectCommentEventType,
	ObjectCommentEvent
>;

export type TimelineEvent =
	| ChangeTimelineEvent
	| IncidentTimelineEvent
	| ObjectUpdateTimelineEvent
	| ObjectCommentTimelineEvent;

export type TimelineEventsServiceError =
	| 'INTERNAL_ERROR'
	| 'VALIDATION_ERROR'
	| 'INVALID_RESPONSE'
	| 'CLIENT_FETCH_ERROR';

export const toTimelineEventsServiceError = (error: string): TimelineEventsServiceError | null => {
	switch (error) {
		case 'INTERNAL_ERROR':
			return 'INTERNAL_ERROR';
		case 'VALIDATION_ERROR':
			return 'VALIDATION_ERROR';
		case 'INVALID_RESPONSE':
			return 'INVALID_RESPONSE';
		case 'CLIENT_FETCH_ERROR':
			return 'CLIENT_FETCH_ERROR';
		default:
			return null;
	}
};

export interface TimelineEventsServiceResponseData {
	data: TimelineEvent[];
}

export interface TimelineEventsServiceResponseError {
	error: TimelineEventsServiceError;
	rawError: Error;
}

export interface UpdateObjectCommentServiceResponseData {
	data: ObjectCommentTimelineEvent;
}

export interface AddObjectCommentServiceResponseData {
	data: ObjectCommentTimelineEvent;
}

export type TimelineEventsServiceResponse =
	| TimelineEventsServiceResponseData
	| TimelineEventsServiceResponseError;

export type AddObjectCommentServiceResponse =
	| AddObjectCommentServiceResponseData
	| TimelineEventsServiceResponseError;

export type DeleteObjectCommentServiceResponse = undefined | TimelineEventsServiceResponseError;

export type UpdateObjectCommentServiceResponse =
	| UpdateObjectCommentServiceResponseData
	| TimelineEventsServiceResponseError;

export const isTimelineEventsServiceResponseData = (
	serviceResponse: unknown,
): serviceResponse is TimelineEventsServiceResponseData =>
	typeof serviceResponse === 'object' && serviceResponse != null && 'data' in serviceResponse;

export const isUpdateObjectCommentServiceResponseData = (
	serviceResponse: unknown,
): serviceResponse is UpdateObjectCommentServiceResponseData =>
	typeof serviceResponse === 'object' && serviceResponse != null && 'data' in serviceResponse;

export const isAddObjectCommentServiceResponseData = (
	serviceResponse: unknown,
): serviceResponse is UpdateObjectCommentServiceResponseData =>
	typeof serviceResponse === 'object' && serviceResponse != null && 'data' in serviceResponse;

export const isTimelineEventsServiceResponseError = (
	serviceResponse: unknown,
): serviceResponse is TimelineEventsServiceResponseError =>
	typeof serviceResponse === 'object' && serviceResponse != null && 'error' in serviceResponse;
